/** Enemy system: spawning (waves and Necromancer minions), movement with statuses, burns, bombs, boosts and boss hits (healers and shield walls: ./enemyAuras). */
import { BossMechanic, DamageType, TileKind, bossHpMultOf } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { Enemy, EnemyAct, Tower } from "../entities";
import { SimEventKind } from "../events";
import { SimConstants } from "../constants";
import { distanceSquared, positionAt } from "../../world/roadGeometry";
import { BossRules, LevelPhase, LevelSim } from "../levelSim";
import { applyDamage } from "./combat";
import { SpawnEntry } from "../waveSchedule";
import { hexedHeal } from "./hex";
import { burrowStep, standsStill } from "./enemyAbilities";
import { clearBranchStatuses } from "../branches/statuses";
import { isLiveRare, onRareEscaped, onRareSpawned } from "../rares";
import { isLiveWyrm, onWyrmFled, onWyrmSpawned } from "../wyrm";
import { isLiveWildPet, onWildPetEscaped, onWildPetSpawned } from "../wildPet";

/** Polymorph's speed on a sheep (the skill's own number; one Polymorph in the game). */
function sheepSpeedMult(sim: LevelSim): Fix64 {
  const s = sim.balance.branches.skills.find((k) => k.id === "polymorph");
  return s?.params.num("speedMult", Fix64.One) ?? Fix64.One;
}

export { tickHealers, tickShields } from "./enemyAuras";

/** Clears the per-tick statuses and the ability state (ENEMIES_V2) a fresh enemy must not inherit from its slot's last occupant. */
export function clearTickStatuses(e: Enemy): void {
  e.hexBonus = Fix64.Zero; e.hexHealCut = Fix64.Zero; e.hexTowerId = 0; e.blocked = false;
  e.act = EnemyAct.None; e.actTicks = 0; e.abilityCooldown = -1; e.burrowsUsed = 0; e.surfaceAt = Fix64.Zero; e.summonerId = 0;
  clearBranchStatuses(e);                                 // tier 4: stuns, poison, sheep, aura slows (sim/branches)
  e.immunity = 0; e.immuneNotes.length = 0;               // M36: a split, an imp or a minion is never immune
  e.hitFraction = null;                                   // Pets v2: only the wild pet bites with its own share
  e.petBurnPerTick = Fix64.Zero; e.petBurnTicks = 0; e.markTaken = Fix64.Zero; e.markPointsPercent = 0; e.markTicks = 0; e.dazzleTicks = 0;   // Pets v3
}

/** Enemy slots kept free for enemies that appear outside the schedule (splits, boss minions). */
const SPAWN_RESERVE = 64;

export function spawnDue(sim: LevelSim): void {
  const t = sim.tick - sim.stageStartTick;
  // A full field holds the portal (Iron's one wave and late Endless waves can outrun a weak defence): the spawns wait
  // until slots free, leaving room for split and summoned enemies. No plain level comes near it.
  while (sim.spawnCursor < sim.schedule.length && sim.schedule[sim.spawnCursor].tick <= t && sim.aliveEnemies < SimConstants.MaxEnemies - SPAWN_RESERVE) {
    spawn(sim, sim.schedule[sim.spawnCursor]);
    sim.spawnCursor++;
  }
}

export function spawn(sim: LevelSim, entry: SpawnEntry): void {
  const a = sim.balance.enemies[entry.archetype];
  const e = sim.allocateEnemy();
  e.id = sim.nextEnemyId++;
  e.archetype = entry.archetype;
  e.alive = true;
  e.isAir = a.isAir;
  e.isBoss = entry.isBoss;
  e.isRare = entry.rare === true;
  e.affix = entry.affix ?? -1;
  e.power = entry.hpMult;
  const elite = e.affix >= 0 ? sim.balance.affixes[e.affix] : null;
  e.maxHp = a.hp.mul(entry.hpMult);
  if (elite !== null && !elite.hpMult.eq(Fix64.One)) e.maxHp = e.maxHp.mul(elite.hpMult);
  e.hp = e.maxHp;
  e.speedPerTick = a.speedCellsPerSecond.divInt(SimConstants.TicksPerSecond);
  if (entry.speedMult !== undefined) e.speedPerTick = e.speedPerTick.mul(entry.speedMult);
  e.armour = a.armour;
  e.distance = Fix64.Zero;
  e.points = entry.pointsOverride >= 0 ? entry.pointsOverride : a.points;
  if (elite !== null && !elite.pointsMult.eq(Fix64.One)) e.points = Fix64.fromInt(e.points).mul(elite.pointsMult).floorToInt();
  // A boss costs the base at least the boss leak damage, whichever archetype wears the crown.
  e.leakDamage = entry.isBoss ? Math.max(a.leakDamage, sim.balance.economy.bossLeakDamage) : a.leakDamage;
  e.slowFraction = Fix64.Zero;
  e.slowTicksLeft = 0;
  clearBurns(e);
  e.shredStacks = 0;
  e.shredArmour = Fix64.Zero;
  e.boostTicksLeft = 0;
  e.boostCooldownLeft = 0;
  e.hitsTaken = 0;
  e.tolled = false;
  e.attackCooldown = 0;
  e.shieldArmour = Fix64.Zero;
  clearTickStatuses(e);
  e.immunity = entry.immunity ?? 0;                       // M36 immune units (sim/immunity.ts)
  [e.x, e.y] = positionAt(sim.road.roadCells, Fix64.Zero);
  e.cx = e.x.floorToInt();
  e.cy = e.y.floorToInt();
  sim._aliveEnemies++;
  sim.events.add(SimEventKind.EnemySpawned, e.id, e.archetype, entry.isBoss ? 1 : 0);
  if (elite !== null) sim.events.add(SimEventKind.EliteSpawned, e.id, elite.index);
  if (entry.wyrm === true) onWyrmSpawned(sim, e);         // M40: its HP from the board, WyrmSpawned (sim/wyrm.ts)
  else if (entry.wildPet === true) onWildPetSpawned(sim, e);   // Pets v2: WildPetEngaged, harmless at the gate (sim/wildPet.ts)
  else if (entry.rare === true) onRareSpawned(sim, e);    // M36: RareSpawned, not BossSpawned (sim/rares.ts)
  else if (entry.isBoss) sim.events.add(SimEventKind.BossSpawned, e.id, e.archetype, a.bossMechanic);
}

/** Necromancer: a Grunt (enemy archetype 0) appears at the boss's position, at the boss's power. */
export function spawnMinion(sim: LevelSim, boss: Enemy): void {
  const a = sim.balance.enemies[0];
  const e = sim.allocateEnemy();
  e.id = sim.nextEnemyId++;
  e.archetype = 0;
  e.alive = true;
  e.isAir = a.isAir;
  e.isBoss = false;
  e.isRare = false;
  e.affix = -1;
  e.maxHp = a.hp.mul(boss.maxHp.div(Fix64.max(sim.balance.enemies[boss.archetype].hp, Fix64.One)).div(bossHpMultOf(sim.balance, sim.level)));
  e.power = e.maxHp.div(Fix64.max(a.hp, Fix64.One));
  e.hp = e.maxHp;
  e.speedPerTick = a.speedCellsPerSecond.divInt(SimConstants.TicksPerSecond);
  e.armour = a.armour;
  e.distance = boss.distance;
  e.points = a.points;
  e.leakDamage = a.leakDamage;
  e.slowFraction = Fix64.Zero; e.slowTicksLeft = 0;
  clearBurns(e);
  e.shredStacks = 0; e.shredArmour = Fix64.Zero; e.boostTicksLeft = 0; e.boostCooldownLeft = 0; e.hitsTaken = 0; e.tolled = boss.tolled; e.attackCooldown = 0; e.shieldArmour = Fix64.Zero;
  clearTickStatuses(e);
  e.x = boss.x; e.y = boss.y; e.cx = boss.cx; e.cy = boss.cy;
  sim._aliveEnemies++;
  sim.events.add(SimEventKind.EnemySpawned, e.id, e.archetype, 0);
}

/**
 * The way enemy `e` covers this tick at its state now — burrowed, mud, a Warlord's aura (`warlords` within `wr2`), a
 * slow, a sheep, a boost — without changing anything: moveEnemies' own step (it counts the effects down after), and the
 * pets' forecast of who walks into reach (sim/petForecast.ts). A held, casting, stunned enemy does not move at all.
 */
export function enemyStep(sim: LevelSim, e: Enemy, warlords: readonly Enemy[], wr2: Fix64): Fix64 {
  let step = burrowStep(sim, e, e.speedPerTick);
  if (sim.tileUnder(e) === TileKind.Mud && !e.isAir && !e.burrowed) step = step.mul(sim.balance.cards.mudSpeedMult);
  for (const w of warlords) {
    if (w === e) continue;
    if (distanceSquared(w.x, w.y, e.x, e.y).le(wr2)) { step = step.mul(BossRules.warlordSpeedMult); break; }
  }
  // A tier-4 aura or zone slow (auraSlow, zero before any branch) does not stack with a Frost hit's: the larger applies.
  if (e.slowTicksLeft > 0) step = step.mul(Fix64.One.sub(Fix64.max(e.slowFraction, e.auraSlow)));
  else if (e.auraSlow.isPositive()) step = step.mul(Fix64.One.sub(e.auraSlow));
  if (e.sheepTicksLeft > 0) step = step.mul(sheepSpeedMult(sim));
  if (e.boostTicksLeft > 0) step = step.mul(sim.balance.enemies[e.archetype].boostSpeedMult);
  return step;
}

/** The Warlords whose aura speeds the enemies round them (moveEnemies), and that aura's reach squared. */
export function warlordsOf(sim: LevelSim): Enemy[] { return sim.enemySlots.filter((b) => b.alive && sim.balance.enemies[b.archetype].bossMechanic === BossMechanic.Warlord); }
export function warlordReach2(): Fix64 { return BossRules.warlordRadius.mul(BossRules.warlordRadius); }

export function moveEnemies(sim: LevelSim): void {
  const length = sim.road.length;
  const warlords = warlordsOf(sim), wr2 = warlordReach2();
  for (const e of sim.enemySlots) {
    if (!e.alive) continue;
    const ea = sim.balance.enemies[e.archetype];
    // Harpy Queen: boosts on a fixed cycle regardless of HP.
    if (ea.bossMechanic === BossMechanic.HarpyQueen && e.boostTicksLeft === 0 && e.boostCooldownLeft === 0 && ea.boostDurationTicks > 0) {
      e.boostTicksLeft = ea.boostDurationTicks; e.boostCooldownLeft = ea.boostCooldownTicks;
      sim.events.add(SimEventKind.EnemyBoosted, e.id, ea.boostDurationTicks);
    }
    // Tier 4 (sim/branches): a stunned, frozen or rooted enemy stands still and strikes no one.
    if (e.stunTicksLeft > 0) { e.stunTicksLeft--; e.blocked = false; continue; }
    e.blocked = sim.troops.blocks(e);
    if (e.blocked || standsStill(e)) continue;          // held, or casting / winding up / digging (ENEMIES_V2)
    const step = enemyStep(sim, e, warlords, wr2);
    if (sim.tileUnder(e) === TileKind.Toll) e.tolled = true;
    if (e.slowTicksLeft > 0) e.slowTicksLeft--;
    if (e.boostTicksLeft > 0) e.boostTicksLeft--;
    if (e.boostCooldownLeft > 0) e.boostCooldownLeft--;
    e.distance = e.distance.add(step);
    if (e.distance.ge(length)) {
      e.alive = false;
      sim._aliveEnemies--;
      if (isLiveWyrm(sim, e)) { onWyrmFled(sim, e); continue; }   // M40: it flies off: no leak, no damage, the choice opens
      if (isLiveWildPet(sim, e)) { onWildPetEscaped(sim, e); continue; }   // Pets v2: it gets away: no leak, no damage
      sim.leaks++;
      // at the gate: a Bomber's bomb falls from where it leaked, not from where it stood last tick
      [e.x, e.y] = positionAt(sim.road.roadCells, length);
      e.cx = e.x.floorToInt(); e.cy = e.y.floorToInt();
      dropBomb(sim, e);
      if (isLiveRare(sim, e)) onRareEscaped(sim, e);       // M36: a rare takes all the fortress's health
      if (!sim.debugNoDefeat) sim.baseHp -= e.leakDamage;
      sim.events.add(SimEventKind.EnemyLeaked, e.id, e.leakDamage);
      sim.events.add(SimEventKind.BaseDamaged, sim.baseHp);
      if (sim.baseHp <= 0) {
        sim.baseHp = 0;
        sim.endLevel(LevelPhase.Lost);
        return;
      }
      continue;
    }
    [e.x, e.y] = positionAt(sim.road.roadCells, e.distance);
    e.cx = e.x.floorToInt();
    e.cy = e.y.floorToInt();
  }
}

/**
 * Burns tick once per tick: every stack deals its damage (summed into one hit, credited to the tower that lit a stack
 * last) and runs down; spent stacks drop off. A stack lit for N ticks deals N ticks of damage.
 */
export function tickBurns(sim: LevelSim): void {
  for (const e of sim.enemySlots) {
    if (!e.alive || e.burnTicksLeft <= 0) continue;
    const damage = e.burnPerTick;
    for (let i = 0; i < e.burnStackTicks.length; i++) e.burnStackTicks[i]--;
    for (let i = e.burnStackTicks.length - 1; i >= 0; i--) if (e.burnStackTicks[i] <= 0) removeBurnStack(e, i);
    summariseBurns(e);
    if (sim.balance.enemies[e.archetype].bossMechanic === BossMechanic.PlagueLord) {
      // Plague Lord: burns heal it instead.
      const amount = Fix64.min(hexedHeal(null, e, damage), e.maxHp.sub(e.hp));
      e.hp = e.hp.add(amount);
      sim.events.add(SimEventKind.EnemyHealed, e.id, 0, amount.floorToInt());
      continue;
    }
    applyDamage(sim, e, sim.findTower(e.burnSourceTowerId), damage, DamageType.Burn);
  }
}

/**
 * Lights or refreshes a burn from `source` (DESIGN §6.1): a tower's own stack is refreshed at its current strength;
 * a new tower adds a stack while fewer than `maxStacks` burn, otherwise it takes over the stack closest to running out
 * (the first such on a tie). So two Fire towers burn twice as hard, and one tower firing fast never stacks with itself.
 */
export function igniteBurn(e: Enemy, source: number, perTick: Fix64, ticks: number, maxStacks: number): void {
  let slot = e.burnStackSource.indexOf(source);
  if (slot < 0 && e.burnStackSource.length < Math.max(1, maxStacks)) {
    slot = e.burnStackSource.length;
    e.burnStackSource.push(source); e.burnStackPerTick.push(perTick); e.burnStackTicks.push(ticks);
  } else if (slot < 0) {
    slot = 0;
    for (let i = 1; i < e.burnStackTicks.length; i++) if (e.burnStackTicks[i] < e.burnStackTicks[slot]) slot = i;
  }
  e.burnStackSource[slot] = source; e.burnStackPerTick[slot] = perTick; e.burnStackTicks[slot] = ticks;
  e.burnSourceTowerId = source;
  summariseBurns(e);
}

/** Puts the enemy out: no stacks, nothing burning. */
export function clearBurns(e: Enemy): void {
  e.burnStackSource.length = 0; e.burnStackPerTick.length = 0; e.burnStackTicks.length = 0;
  e.burnPerTick = Fix64.Zero; e.burnTicksLeft = 0; e.burnSourceTowerId = 0;
}

function removeBurnStack(e: Enemy, i: number): void {
  e.burnStackSource.splice(i, 1); e.burnStackPerTick.splice(i, 1); e.burnStackTicks.splice(i, 1);
}

/** Recomputes the stack summary: total damage per tick and the longest time left. */
function summariseBurns(e: Enemy): void {
  let total = Fix64.Zero, left = 0;
  for (let i = 0; i < e.burnStackTicks.length; i++) { total = total.add(e.burnStackPerTick[i]); left = Math.max(left, e.burnStackTicks[i]); }
  e.burnPerTick = total; e.burnTicksLeft = left;
}

/**
 * Bomber: the nearest tower to where it died or leaked is disabled for the archetype's bomb duration (ties → lower id,
 * the base included). The bomb is dropped, not aimed: a tower farther than `bombRange` from that spot is out of reach,
 * and with none in reach the bomb is wasted (BombDropped with tower 0, no TowerDisabled). Measured tower cell to the
 * bomber's exact position, squared, in fixed point.
 */
export function dropBomb(sim: LevelSim, e: Enemy): void {
  const a = sim.balance.enemies[e.archetype];
  if (a.bombDisableTicks <= 0) return;
  const limited = a.bombRange.isPositive();
  const reach2 = a.bombRange.mul(a.bombRange);
  let best: Tower | null = null; let bestD2 = Fix64.Zero;
  for (const t of sim.towerSlots) {
    if (!t.alive) continue;
    const d2 = distanceSquared(Fix64.fromInt(t.cell.x), Fix64.fromInt(t.cell.y), e.x, e.y);
    if (limited && d2.gt(reach2)) continue;
    if (best === null || d2.lt(bestD2) || (d2.eq(bestD2) && t.id < best.id)) { best = t; bestD2 = d2; }
  }
  sim.events.add(SimEventKind.BombDropped, e.id, best?.id ?? 0);
  if (best === null) return;
  best.disabledTicksLeft = Math.max(best.disabledTicksLeft, a.bombDisableTicks);
  sim.events.add(SimEventKind.TowerDisabled, best.id, a.bombDisableTicks);
}

/** Flyer boost: once its HP drops below the threshold, dash for the duration; may not trigger again until the cooldown has run. */
export function maybeBoost(sim: LevelSim, e: Enemy): void {
  const a = sim.balance.enemies[e.archetype];
  if (a.boostDurationTicks <= 0 || !e.hp.isPositive()) return;
  if (e.boostCooldownLeft > 0 || e.boostTicksLeft > 0) return;
  if (e.hp.ge(e.maxHp.mul(a.boostThreshold))) return;
  e.boostTicksLeft = a.boostDurationTicks;
  e.boostCooldownLeft = a.boostCooldownTicks;
  sim.events.add(SimEventKind.EnemyBoosted, e.id, a.boostDurationTicks);
}

export function onBossHit(sim: LevelSim, e: Enemy, mechanic: BossMechanic): void {
  switch (mechanic) {
    case BossMechanic.Necromancer:
      if (e.hitsTaken % BossRules.necromancerHitsPerSpawn === 0) spawnMinion(sim, e);
      return;
    case BossMechanic.Ironclad: {
      // Armour climbs linearly with missing HP.
      const missing = Fix64.One.sub(e.hp.div(e.maxHp));
      e.armour = Fix64.clamp(sim.balance.enemies[e.archetype].armour.add(missing.mul(BossRules.ironcladArmourGain)), Fix64.Zero, Fix64.One);
      return;
    }
    default: return;
  }
}
